import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, ScrollView, Alert, ActivityIndicator } from 'react-native';
import AppTextInput from './AppTextInput';
import type { CareCircleMember, CareCircleRole, CareMemberPermissions } from '../types/care';
import { careCircleService } from '../services/careCircleService';
import { sendCareInvite } from '../services/careInvitationService';

interface CareTeamModalProps {
  visible: boolean;
  recipientId: string;
  /** Recipient name for invite message. */
  recipientName?: string;
  /** Name of the person sending the invite. */
  inviterName?: string;
  onClose: () => void;
  onUpdated: () => void;
}

const ROLES: { value: CareCircleRole; label: string }[] = [
  { value: 'coordinator', label: 'Coordinator' },
  { value: 'family', label: 'Family / Sibling' },
  { value: 'caregiver', label: 'Caregiver' },
];

const CareTeamModal: React.FC<CareTeamModalProps> = ({
  visible,
  recipientId,
  recipientName = 'Care recipient',
  inviterName = 'A family member',
  onClose,
  onUpdated,
}) => {
  const [members, setMembers] = useState<CareCircleMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addName, setAddName] = useState('');
  const [addPhone, setAddPhone] = useState('');
  const [addRole, setAddRole] = useState<CareCircleRole>('family');
  const [editingPermissions, setEditingPermissions] = useState<string | null>(null);

  useEffect(() => {
    if (visible && recipientId) {
      careCircleService.getMembers(recipientId).then(setMembers).finally(() => setLoading(false));
    }
  }, [visible, recipientId]);

  const handleAdd = async () => {
    const email = addEmail.trim();
    const name = addName.trim() || email;
    const phone = addPhone.trim();
    if (!email && !phone) {
      Alert.alert('Required', 'Enter email and/or phone number to send the invite.');
      return;
    }
    setSending(true);
    try {
      const newMember = await careCircleService.addMember(recipientId, {
        userId: '',
        email: email || '',
        name,
        phone: phone || undefined,
        role: addRole,
        permissions: careCircleService.DEFAULT_PERMISSIONS[addRole],
        status: 'pending',
      });
      const result = await sendCareInvite({
        recipientId,
        recipientName,
        inviterName,
        member: { ...newMember, phone: phone || undefined },
      });
      setAddEmail('');
      setAddName('');
      setAddPhone('');
      setShowAdd(false);
      onUpdated();
      careCircleService.getMembers(recipientId).then(setMembers);
      const sent = result.smsSent || result.emailSent;
      if (sent) {
        Alert.alert('Invite sent', result.smsSent && result.emailSent
          ? 'SMS and email sent.'
          : result.smsSent
            ? 'SMS sent.'
            : 'Email sent.');
      } else if (result.error) {
        Alert.alert('Invite saved', 'Member added to care team. Could not send notification. Share this link: ' + result.inviteUrl);
      } else {
        Alert.alert('Invite saved', 'Member added. Share this link: ' + result.inviteUrl);
      }
    } catch (e) {
      Alert.alert('Error', (e as Error).message);
    } finally {
      setSending(false);
    }
  };

  const handleUpdatePermissions = async (memberId: string, permissions: Partial<CareMemberPermissions>) => {
    try {
      await careCircleService.updateMemberPermissions(recipientId, memberId, permissions);
      setEditingPermissions(null);
      onUpdated();
      careCircleService.getMembers(recipientId).then(setMembers);
    } catch (e) {
      Alert.alert('Error', (e as Error).message);
    }
  };

  const handleRemove = (member: CareCircleMember) => {
    Alert.alert(
      'Remove member',
      `Remove ${member.name} from the care team?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            await careCircleService.removeMember(recipientId, member.id);
            onUpdated();
            careCircleService.getMembers(recipientId).then(setMembers);
          },
        },
      ]
    );
  };

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <View style={styles.card}>
          <View style={styles.header}>
            <Text style={styles.title}>Care team</Text>
            <TouchableOpacity onPress={onClose} hitSlop={12}>
              <Text style={styles.close}>✕</Text>
            </TouchableOpacity>
          </View>
          <Text style={styles.hint}>Add siblings and caregivers. Control who can view, edit, and mark tasks done.</Text>
          {loading ? (
            <Text style={styles.loading}>Loading...</Text>
          ) : (
            <ScrollView style={styles.body}>
              {members.map((m) => (
                <View key={m.id} style={styles.memberRow}>
                  <View style={styles.memberInfo}>
                    <Text style={styles.memberName}>{m.name}</Text>
                    <Text style={styles.memberEmail}>{m.email || m.phone || '—'}</Text>
                    <Text style={styles.memberRole}>{m.role} {m.status === 'pending' ? '(invited)' : ''}</Text>
                  </View>
                  <View style={styles.memberActions}>
                    {editingPermissions === m.id ? (
                      <View style={styles.permissionGrid}>
                        {(['view', 'edit', 'markTasksDone', 'assignTasks'] as const).map((key) => (
                          <TouchableOpacity
                            key={key}
                            style={[styles.permToggle, m.permissions[key] && styles.permToggleOn]}
                            onPress={() => handleUpdatePermissions(m.id, { [key]: !m.permissions[key] })}
                          >
                            <Text style={styles.permLabel}>{key === 'markTasksDone' ? 'Mark done' : key}</Text>
                          </TouchableOpacity>
                        ))}
                        <TouchableOpacity onPress={() => setEditingPermissions(null)}>
                          <Text style={styles.doneEdit}>Done</Text>
                        </TouchableOpacity>
                      </View>
                    ) : (
                      <>
                        <TouchableOpacity onPress={() => setEditingPermissions(m.id)}>
                          <Text style={styles.link}>Permissions</Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => handleRemove(m)}>
                          <Text style={styles.remove}>Remove</Text>
                        </TouchableOpacity>
                      </>
                    )}
                  </View>
                </View>
              ))}
              {!showAdd ? (
                <TouchableOpacity style={styles.addBtn} onPress={() => setShowAdd(true)}>
                  <Text style={styles.addBtnText}>+ Invite family or caregiver</Text>
                </TouchableOpacity>
              ) : (
                <View style={styles.addForm}>
                  <AppTextInput
                    style={styles.input}
                    placeholder="Email (optional)"
                    value={addEmail}
                    onChangeText={setAddEmail}
                    autoCapitalize="none"
                    keyboardType="email-address"
                  />
                  <AppTextInput
                    style={styles.input}
                    placeholder="Phone (optional)"
                    value={addPhone}
                    onChangeText={setAddPhone}
                    keyboardType="phone-pad"
                  />
                  <AppTextInput
                    style={styles.input}
                    placeholder="Name (optional)"
                    value={addName}
                    onChangeText={setAddName}
                  />
                  <View style={styles.roleRow}>
                    {ROLES.map((r) => (
                      <TouchableOpacity
                        key={r.value}
                        style={[styles.roleBtn, addRole === r.value && styles.roleBtnOn]}
                        onPress={() => setAddRole(r.value)}
                      >
                        <Text style={styles.roleBtnText}>{r.label}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                  <View style={styles.addFormActions}>
                    <TouchableOpacity onPress={() => setShowAdd(false)}>
                      <Text style={styles.cancelText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.inviteBtn, sending && styles.inviteBtnDisabled]}
                      onPress={handleAdd}
                      disabled={sending}
                    >
                      {sending ? (
                        <ActivityIndicator size="small" color="#fff" />
                      ) : (
                        <Text style={styles.inviteBtnText}>Send invite</Text>
                      )}
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' },
  card: { backgroundColor: '#fff', borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '85%' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 20 },
  title: { fontSize: 20, fontWeight: '700', color: '#1C1C1E' },
  close: { fontSize: 22, color: '#8E8E93' },
  hint: { fontSize: 12, color: '#8E8E93', paddingHorizontal: 20, paddingBottom: 12 },
  loading: { padding: 20 },
  body: { paddingHorizontal: 20, paddingBottom: 24 },
  memberRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#F2F2F7' },
  memberInfo: { flex: 1 },
  memberName: { fontSize: 16, fontWeight: '600', color: '#1C1C1E' },
  memberEmail: { fontSize: 13, color: '#8E8E93' },
  memberRole: { fontSize: 12, color: '#666', marginTop: 2 },
  memberActions: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  permissionGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 },
  permToggle: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6, backgroundColor: '#F2F2F7' },
  permToggleOn: { backgroundColor: '#007AFF' },
  permLabel: { fontSize: 11, color: '#1C1C1E' },
  doneEdit: { fontSize: 14, color: '#007AFF', fontWeight: '500' },
  link: { fontSize: 14, color: '#007AFF' },
  remove: { fontSize: 14, color: '#FF3B30' },
  addBtn: { marginTop: 16, padding: 14, borderRadius: 10, backgroundColor: '#F2F2F7', alignItems: 'center' },
  addBtnText: { fontSize: 15, fontWeight: '600', color: '#007AFF' },
  addForm: { marginTop: 16 },
  input: { borderWidth: 1, borderColor: '#E5E5E7', borderRadius: 10, padding: 12, marginBottom: 10, fontSize: 16 },
  roleRow: { flexDirection: 'row', gap: 8, marginBottom: 12 },
  roleBtn: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, backgroundColor: '#F2F2F7' },
  roleBtnOn: { backgroundColor: '#007AFF' },
  roleBtnText: { fontSize: 13, color: '#1C1C1E' },
  addFormActions: { flexDirection: 'row', gap: 12 },
  cancelText: { fontSize: 15, color: '#8E8E93' },
  inviteBtn: { backgroundColor: '#007AFF', paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8 },
  inviteBtnDisabled: { opacity: 0.7 },
  inviteBtnText: { fontSize: 15, fontWeight: '600', color: '#fff' },
});

export default CareTeamModal;
